import { useState, useEffect } from 'react';
import { Route, Switch, Redirect, useLocation } from 'wouter';
import { QueryClient, QueryClientProvider, QueryCache } from '@tanstack/react-query';
import { ToastProvider } from '@/components/shared/Toast';
import { TabBar } from '@/components/layout/TabBar';
import { NotificationPermissionModal } from '@/components/shared/NotificationPermissionModal';
import { SchedulePage } from '@/pages/schedule/SchedulePage';
import { EventFormPage } from '@/pages/schedule/EventFormPage';
import { PasteImportPage } from '@/pages/schedule/PasteImportPage';
import { ImportReviewPage } from '@/pages/schedule/ImportReviewPage';
import { LivePage } from '@/pages/live/LivePage';
import { SettingsPage } from '@/pages/settings/SettingsPage';
import { OnboardingPage } from '@/pages/onboarding/OnboardingPage';
import { SharedViewPage } from '@/pages/share/SharedViewPage';
import { usePreferences } from '@/hooks/usePreferences';
import { track } from '@/lib/analytics';

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (_error, query) => {
      const onError = query.meta?.onError;
      if (typeof onError === 'function') void (onError as () => Promise<void>)();
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

function AppContent() {
  const { prefs, isLoading } = usePreferences();
  const [location, navigate] = useLocation();
  const [showPermModal, setShowPermModal] = useState(false);

  // Onboarding redirect
  useEffect(() => {
    if (!isLoading && !prefs.onboardingComplete && location !== '/onboarding') {
      navigate('/onboarding');
    }
  }, [isLoading, prefs.onboardingComplete, location, navigate]);

  // Permission modal after onboarding
  useEffect(() => {
    if (location === '/schedule') {
      const flag = sessionStorage.getItem('wedtrack:show_permission_modal');
      if (flag) {
        sessionStorage.removeItem('wedtrack:show_permission_modal');
        setTimeout(() => setShowPermModal(true), 500);
      }
    }
  }, [location]);

  // Track first app open per session
  useEffect(() => {
    if (!sessionStorage.getItem('wedtrack:app_opened')) {
      sessionStorage.setItem('wedtrack:app_opened', '1');
      track('app_opened');
    }
  }, []);

  if (isLoading) return null;

  const showTabBar =
    location !== '/onboarding' &&
    !location.startsWith('/s/') &&
    location !== '/schedule/import/paste' &&
    location !== '/schedule/import/review' &&
    location !== '/schedule/event/new' &&
    !location.match(/^\/schedule\/event\/.+\/edit$/);

  return (
    <>
      <Switch>
        <Route path="/onboarding" component={OnboardingPage} />
        <Route path="/schedule/import/paste" component={PasteImportPage} />
        <Route path="/schedule/import/review" component={ImportReviewPage} />
        <Route path="/schedule/event/new" component={EventFormPage} />
        <Route path="/schedule/event/:id/edit" component={EventFormPage} />
        <Route path="/schedule" component={SchedulePage} />
        <Route path="/live" component={LivePage} />
        <Route path="/settings" component={SettingsPage} />
        <Route path="/s/:shareId" component={SharedViewPage} />
        <Route>
          <Redirect to="/schedule" />
        </Route>
      </Switch>
      {showTabBar && <TabBar />}
      <NotificationPermissionModal
        isOpen={showPermModal}
        onClose={() => setShowPermModal(false)}
      />
    </>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <div className="max-w-lg mx-auto relative min-h-screen">
          <AppContent />
        </div>
      </ToastProvider>
    </QueryClientProvider>
  );
}
